import { useState } from 'react';
import { X, User2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { DiffViewer } from './DiffViewer';
import { useGetDocumentationChangesQuery } from '@/store/documentationApi';
import { DocumentationChange } from '@/types/Documentation';

interface ChangeHistoryPanelProps {
    docId: number;
    onClose: () => void;
}

export function ChangeHistoryPanel({ docId, onClose }: ChangeHistoryPanelProps) {
    const { data: changes = [], isLoading } = useGetDocumentationChangesQuery(docId);
    const [expanded, setExpanded] = useState<number | null>(null);

    return (
        <div
            className="flex flex-col w-80 rounded-xl border bg-background shadow-sm overflow-hidden shrink-0"
            dir="rtl"
        >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-2 border-b bg-muted/30 shrink-0">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    היסטוריית שינויים
                </span>
                <Button size="icon" variant="ghost" className="h-6 w-6" onClick={onClose}>
                    <X className="h-3.5 w-3.5" />
                </Button>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto">
                {isLoading ? (
                    <div className="flex justify-center py-8">
                        <Spinner />
                    </div>
                ) : changes.length === 0 ? (
                    <p className="text-center py-8 text-sm text-muted-foreground px-4">
                        אין שינויים מתועדים עדיין
                    </p>
                ) : (
                    <ul className="divide-y">
                        {changes.map((change: DocumentationChange) => (
                            <li key={change.id} className="px-4 py-3 hover:bg-muted/20 transition-colors">
                                <div className="flex items-start justify-between gap-2">
                                    <div className="flex items-start gap-2 flex-1 min-w-0">
                                        <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                                            <User2 className="h-3.5 w-3.5" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-foreground truncate">
                                                {change.editor_name || 'לא ידוע'}
                                            </p>
                                            <p className="text-xs text-muted-foreground">
                                                {new Date(change.created_at).toLocaleString('he-IL')}
                                            </p>
                                            {change.description && (
                                                <p className="text-xs text-foreground/80 mt-1.5 leading-relaxed">
                                                    {change.description}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    {change.diff && (
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            className="text-xs h-6 px-2 shrink-0"
                                            onClick={() =>
                                                setExpanded((prev) =>
                                                    prev === change.id ? null : change.id
                                                )
                                            }
                                        >
                                            {expanded === change.id ? 'סגור' : 'diff'}
                                        </Button>
                                    )}
                                </div>

                                {expanded === change.id && change.diff && (
                                    <div className="mt-2">
                                        <DiffViewer diff={change.diff} className="max-h-48" />
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}
